import React from "react";
import { SearchResult } from "@/models/search/SearchResults";
import { getDisplayName } from "@/config/datasources";
import { getResultData } from "./resultDataExtractors";
import { getMetadataDisplayInfo } from "./metadataHelpers";
import { LegislationMetadata } from "@/models/legislation/Legislation";
import { LeveledFiltersConfig } from "@/components/data-table/data-table";

export const getEntitySpecificInfo = (
  result: SearchResult
): { source?: string; parts: (string | React.ReactNode)[] } => {
  const resultData = getResultData(result);
  const info = resultData.entitySpecificInfo;

  if (result.legislation) {
    const parts: (string | React.ReactNode)[] = [];
    let source: string | undefined;

    if (result.legislation.legislationExternalId) {
      parts.push(`Id: ${result.legislation.legislationExternalId}`);
    }

    if (info.source) {
      const mappedSource = getDisplayName(info.source.toString());
      source = mappedSource ?? info.source.toString();
    }

    const metadataInfo = getMetadataDisplayInfo(
      info.metadataObject as LegislationMetadata,
      info.source as string | undefined,
      resultData.title
    );
    parts.push(...metadataInfo);

    return { source, parts };
  }

  if (result.workspace) {
    return {
      parts: [
        `${info.fileCount === 0 ? "Ingen" : info.fileCount} ${
          info.fileCount === 1 ? "fil" : "filer"
        }`,
        `${info.folderCount === 0 ? "Ingen" : info.folderCount} ${
          info.folderCount === 1 ? "mappe" : "mapper"
        }`,
      ],
    };
  }

  if (result.workspaceDocument) {
    const parts: (string | React.ReactNode)[] = [];
    if (info.workspaceName) parts.push(`Workspace: ${info.workspaceName}`);
    if (info.documentType) parts.push(`Filtype: ${info.documentType}`);
    return { parts };
  }

  if (result.chat) {
    return { parts: [`${info.messageCount} beskeder`] };
  }

  return { parts: [] };
};

export const formatDate = (date: Date | string | undefined): string => {
  if (!date) return "";
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return "";

  const day = dateObj.getDate().toString().padStart(2, "0");
  const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
  const year = dateObj.getFullYear().toString().slice(-2);

  return `${day}/${month}/${year}`;
};

// Helper function to get display name for a filter key from leveledFilters
// Supports both namespaced keys ("source::key") and plain keys ("key")
export const getFilterDisplayName = (
  filterKey: string,
  leveledFilters: LeveledFiltersConfig | undefined
): string => {
  if (!leveledFilters) return filterKey;

  // Handle namespaced keys: "sourceKey::filterKey"
  const sepIdx = filterKey.indexOf("::");
  if (sepIdx !== -1) {
    const sourceKey = filterKey.substring(0, sepIdx);
    const originalKey = filterKey.substring(sepIdx + 2);
    const sourceConfig = leveledFilters.levels[sourceKey];
    if (sourceConfig?.filters?.[originalKey]) {
      return sourceConfig.filters[originalKey].displayName || originalKey;
    }
    return originalKey;
  }

  // Fallback: check all sources for plain (non-namespaced) keys
  for (const sourceConfig of Object.values(leveledFilters.levels)) {
    if (sourceConfig.filters && sourceConfig.filters[filterKey]) {
      return sourceConfig.filters[filterKey].displayName || filterKey;
    }
  }

  return filterKey;
};
